import { ReactNode, useCallback } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import { Adg4TextUnderline } from "@/components/adg4/text-underline/src/text-underline";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { useIsMobile } from "@/utils/useIsMobile";

import { ReusableCrossflowHeroComponentProps } from "../reusable-xflow-hero-component/reusable-xflow-hero-templates";
import { ReusableCrossflowHeroComponent } from "../reusable-xflow-hero-component/reusable-xflow-hero-templates";

const JSM_UNDERLINE_SVG = (
    <svg
        width="100%"
        height="30"
        viewBox="0 0 306 42"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M0.314542 22.3672C52.9464 14.9125 91.8245 33.6666 144.966 18.1254C147.46 24.7349 153.892 33.0058 163.497 33.1094C155.486 32.9725 142.641 15.7178 155.536 27.2686C163.119 36.0683 196.172 25.2364 170.842 32.944C188.334 31.2885 204.356 20.5339 216.163 10.9702C235.811 -2.65832 220.928 23.6737 235.178 36.8756C255.37 55.2242 316.463 7.29572 304.134 7.28816C296.296 13.2947 285.769 17.046 281.423 23.6985C204.757 63.1757 259.992 -9.72674 214.311 2.55426C139.744 53.2533 177.393 7.4163 133.572 10.6223C108.06 28.9946 -0.613806 0.46356 0.29434 22.3672L0.314542 22.3672Z"
            fill="#FFA900"
        />
    </svg>
);

const JsmTextFormatter = ({ children }: { children: ReactNode }) => (
    <Adg4TextUnderline underline={JSM_UNDERLINE_SVG}>
        {children}
    </Adg4TextUnderline>
);

const subtitleStyles = css({
    margin: 0,
    textAlign: "center",
    marginBlockStart: "16px",
    marginBlockEnd: "16px",

    [media.below.sm]: {
        paddingInline: "32px"
    }
});

const JSMHeroTitle = () => {
    const isMobile = useIsMobile(430);
    const fontWeight = isMobile ? "bold" : "normal";
    const jsmFormatter = useCallback(
        (str: ReactNode) => <JsmTextFormatter>{str}</JsmTextFormatter>,
        []
    );

    return (
        <div style={{ fontWeight }}>
            <FormattedMessage
                id="HomePageTemplateV2.jsmCrossflowHero"
                defaultMessage="Unlock teams with{br} <jsm>Jira Service Management</jsm>"
                values={{
                    jsm: jsmFormatter,
                    br: <br />
                }}
            />
        </div>
    );
};

const JSMHeroVariantTitle = () => {
    const isMobile = useIsMobile(430);
    const fontWeight = isMobile ? "bold" : "normal";
    const jsmFormatter = useCallback(
        (str: ReactNode) => <JsmTextFormatter>{str}</JsmTextFormatter>,
        []
    );

    return (
        <div style={{ fontWeight }}>
            <FormattedMessage
                id="HomePageTemplateV2.jsmCrossflowHero2"
                defaultMessage="Deliver great service with{br} <jsm>Jira Service Management</jsm>"
                values={{
                    jsm: jsmFormatter,
                    br: <br />
                }}
            />
        </div>
    );
};

const JSMSubtitle = () => (
    <p className="body-lg" css={subtitleStyles}>
        <FormattedMessage
            id="HomePageTemplateV2.crossflowHero.jsmSubtitle"
            defaultMessage="Jira Service Management is now part of Service Collection."
        />
    </p>
);

export const JSMCrossflowComponent = ({
    contentComponent,
    gwp10965XflowRecommendation
}: {
    contentComponent?: React.ComponentType<Record<string, never>>;
    segmentName?: string;
    sectionId?: string;
    scrollTrackingSection?: string;
    gwp10965XflowRecommendation?: boolean;
}) => {
    const intl = useIntl();
    const jsmData: ReusableCrossflowHeroComponentProps = {
        heroTitle: gwp10965XflowRecommendation ? (
            <JSMHeroVariantTitle />
        ) : (
            <JSMHeroTitle />
        ),
        ctaButton: {
            buttonText: intl.formatMessage({
                id: "HomePageTemplateV2.jsmCrossflowHero.ctaButtonText",
                defaultMessage: "Get started"
            }),
            buttonColor: "blue",
            buttonHref: useLocalizedHref(
                "/try/cloud/signup?bundle=jira-service-management"
            ),
            buttonVariant: "primary",
            eventProps: {
                label: "Get started",
                labelEnglish: "Get started"
            }
        },
        contentComponent,
        segmentName: "jsm-crossflow-hero",
        sectionId: "jsm-crossflow-hero",
        scrollTrackingSection: "jsm-crossflow-hero",
        gwp10965XflowRecommendation,
        heroSubtitle: gwp10965XflowRecommendation ? <JSMSubtitle /> : undefined
    };

    return <ReusableCrossflowHeroComponent {...jsmData} />;
};
